// PANTAU data fetchers - BMKG (via backend proxy), USGS, NASA EONET, Smithsonian, plate GeoJSON.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const USGS = {
  hour_all: "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/all_hour.geojson",
  day_25: "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/2.5_day.geojson",
  day_45: "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_day.geojson",
  week_45: "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/4.5_week.geojson",
  month_significant: "https://earthquake.usgs.gov/earthquakes/feed/v1.0/summary/significant_month.geojson",
};

export const USGS_FEEDS = [
  { id: "hour_all", label: "Semua Gempa (1 Jam)" },
  { id: "day_25", label: "M 2.5+ (24 Jam)" },
  { id: "day_45", label: "M 4.5+ (24 Jam)" },
  { id: "week_45", label: "M 4.5+ (7 Hari)" },
  { id: "month_significant", label: "Signifikan (30 Hari)" },
];

// External GeoJSON layers (public GitHub / open data)
const LAYER_URLS = {
  plates: "https://raw.githubusercontent.com/fraxen/tectonicplates/master/GeoJSON/PB2002_boundaries.json",
  faults: "https://raw.githubusercontent.com/fraxen/tectonicplates/master/GeoJSON/PB2002_orogens.json",
  volcanoes: "https://raw.githubusercontent.com/fraxen/tectonicplates/master/GeoJSON/PB2002_steps.json",
  // Smithsonian volcanoes via a curated GVP-derived list
  volcanoes_gvp: "https://raw.githubusercontent.com/dracos/volcanoes/master/volcanoes.geo.json",
};

// ---------- USGS ----------
export async function fetchUSGS(feedId = "day_45") {
  const url = USGS[feedId] || USGS.day_45;
  const res = await fetch(url);
  if (!res.ok) throw new Error("USGS fetch failed");
  const data = await res.json();
  return (data.features || []).map((f) => {
    const [lng, lat, depth] = f.geometry.coordinates;
    return {
      id: f.id,
      magnitude: f.properties.mag,
      location: f.properties.place || "Unknown",
      depth: depth ?? 0,
      time: f.properties.time,
      latitude: lat,
      longitude: lng,
      source: "USGS",
      tsunami: !!f.properties.tsunami,
      felt: f.properties.felt || null,
      mmi: f.properties.mmi || null,
      status: f.properties.status || "reviewed",
      url: f.properties.url,
    };
  });
}

// ---------- BMKG via backend proxy ----------
function parseBMKGCoord(s) {
  if (!s) return 0;
  const num = parseFloat(String(s).replace(/[^\d.\-]/g, ""));
  if (/S|LS/i.test(String(s))) return -Math.abs(num);
  if (/W|BB/i.test(String(s))) return -Math.abs(num);
  return num;
}

function parseBMKGDateTime(dateStr, jamStr) {
  // BMKG dateStr: "07 Feb 2026", jam: "12:34:56 WIB" -> ISO UTC (WIB = UTC+7)
  try {
    if (!dateStr || !jamStr) return Date.now();
    const months = ["Jan","Feb","Mar","Apr","Mei","Jun","Jul","Agu","Sep","Okt","Nov","Des"];
    const monthsEn = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const [d, monRaw, y] = dateStr.split(" ");
    let mi = months.findIndex((m) => m.toLowerCase() === monRaw.toLowerCase());
    if (mi < 0) mi = monthsEn.findIndex((m) => m.toLowerCase() === monRaw.toLowerCase());
    const [hh, mm, ss] = (jamStr.split(" ")[0] || "00:00:00").split(":").map(Number);
    const utcMs = Date.UTC(parseInt(y), mi, parseInt(d), hh - 7, mm, ss || 0);
    return utcMs;
  } catch {
    return Date.now();
  }
}

function bmkgGempaToObj(g, source = "BMKG") {
  const coordArr = (g.point && g.point.coordinates ? String(g.point.coordinates).split(",") : []);
  const lng = coordArr[0] ? parseFloat(coordArr[0]) : parseBMKGCoord(g.Bujur);
  const lat = coordArr[1] ? parseFloat(coordArr[1]) : parseBMKGCoord(g.Lintang);
  return {
    id: `bmkg-${g.DateTime || g.Tanggal + "-" + g.Jam}-${g.Magnitude}`,
    magnitude: parseFloat(g.Magnitude) || 0,
    location: g.Wilayah || g.Keterangan || "Wilayah tidak diketahui",
    depth: parseFloat(String(g.Kedalaman || "0").replace(/[^\d.]/g, "")) || 0,
    time: parseBMKGDateTime(g.Tanggal, g.Jam),
    latitude: lat,
    longitude: lng,
    source,
    tsunami: g.Potensi && !/tidak/i.test(g.Potensi) ? g.Potensi : false,
    felt: g.Dirasakan || null,
    mmi: null,
    status: "reviewed",
    url: null,
  };
}

export async function fetchBMKG(kind = "autogempa") {
  try {
    const res = await fetch(`${API}/bmkg/${kind}`);
    if (!res.ok) throw new Error("BMKG proxy fail");
    const data = await res.json();
    if (kind === "autogempa") {
      const g = data.Infogempa?.gempa;
      return g ? [bmkgGempaToObj(g)] : [];
    }
    const arr = data.Infogempa?.gempa || [];
    return arr.map((g) => bmkgGempaToObj(g));
  } catch (e) {
    console.warn("BMKG fetch failed, falling back to USGS:", e.message);
    return null; // signal fallback
  }
}

// Wildfires: combined FIRMS (with FRP/confidence/sensor) + EONET (open events).
export async function fetchFires(region = "southeast_asia", minFrp = 0, days = "24h") {
  try {
    const [firmsRes, eonetRes] = await Promise.all([
      fetch(`${API}/firms?region=${region}&days=${days}&min_frp=${minFrp}&limit=6000`).then((r) => r.ok ? r.json() : { points: [] }),
      fetch(`${API}/eonet/wildfires?days=${days === "7d" ? 7 : 1}&limit=200`).then((r) => r.ok ? r.json() : { events: [] }),
    ]);

    const firmsPoints = (firmsRes.points || []).map((p) => ({
      id: p.id,
      latitude: p.latitude,
      longitude: p.longitude,
      frp: p.frp,
      brightness: p.brightness,
      confidence: p.confidence,
      date: p.date,
      daynight: p.daynight,
      satellite: p.satellite,
      instrument: p.instrument,
      source: "FIRMS",
      title: `${p.instrument} · ${p.confidence} · FRP ${p.frp} MW`,
    }));

    const eonetPoints = [];
    (eonetRes.events || []).forEach((ev) => {
      (ev.geometry || []).forEach((geo, idx) => {
        if (geo.type === "Point") {
          eonetPoints.push({
            id: `${ev.id}-${idx}`,
            title: ev.title,
            latitude: geo.coordinates[1],
            longitude: geo.coordinates[0],
            date: geo.date,
            source: "EONET",
            confidence: "nominal",
            frp: null,
            instrument: (ev.sources?.[0]?.id) || "EONET",
          });
        }
      });
    });

    return [...firmsPoints, ...eonetPoints];
  } catch (e) {
    console.warn("fetchFires failed:", e);
    return [];
  }
}

// Backward-compat alias
export const fetchWildfires = () => fetchFires();

// PVMBG live volcano alertLevels (best-effort). Returns map keyed by lowercase volcano name.
export async function fetchPVMBG() {
  try {
    const r = await fetch(`${API}/pvmbg/volcanoes`);
    if (!r.ok) return {};
    const data = await r.json();
    const map = {};
    (data.volcanoes || []).forEach((v) => {
      map[v.name.toLowerCase()] = { alertLevel: v.alertLevel, label: v.label, updated: v.updated };
    });
    return map;
  } catch {
    return {};
  }
}

// Major Indonesia + regional cities for nearest-settlement distance (haversine on client)
export const CITIES = [
  { name: "Jakarta", lat: -6.2088, lng: 106.8456 },
  { name: "Surabaya", lat: -7.2575, lng: 112.7521 },
  { name: "Bandung", lat: -6.9175, lng: 107.6191 },
  { name: "Medan", lat: 3.5952, lng: 98.6722 },
  { name: "Semarang", lat: -6.9932, lng: 110.4203 },
  { name: "Makassar", lat: -5.1477, lng: 119.4327 },
  { name: "Palembang", lat: -2.9909, lng: 104.7566 },
  { name: "Pekanbaru", lat: 0.5333, lng: 101.45 },
  { name: "Jambi", lat: -1.6101, lng: 103.6131 },
  { name: "Pontianak", lat: -0.0263, lng: 109.3425 },
  { name: "Palangkaraya", lat: -2.2136, lng: 113.9108 },
  { name: "Banjarmasin", lat: -3.3186, lng: 114.5944 },
  { name: "Balikpapan", lat: -1.2379, lng: 116.8529 },
  { name: "Samarinda", lat: -0.5017, lng: 117.1536 },
  { name: "Manado", lat: 1.4748, lng: 124.8421 },
  { name: "Denpasar", lat: -8.65, lng: 115.2167 },
  { name: "Padang", lat: -0.9471, lng: 100.4172 },
  { name: "Jayapura", lat: -2.5337, lng: 140.7181 },
  { name: "Banda Aceh", lat: 5.5483, lng: 95.3238 },
  { name: "Kuala Lumpur", lat: 3.139, lng: 101.6869 },
  { name: "Singapore", lat: 1.3521, lng: 103.8198 },
  { name: "Manila", lat: 14.5995, lng: 120.9842 },
  { name: "Bangkok", lat: 13.7563, lng: 100.5018 },
];

export function haversineKm(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 + Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(a));
}

export function nearestCity(lat, lng) {
  let best = null;
  for (const c of CITIES) {
    const d = haversineKm(lat, lng, c.lat, c.lng);
    if (!best || d < best.distanceKm) best = { name: c.name, distanceKm: d };
  }
  return best;
}

// ---------- GeoJSON layers (cached) ----------
const geoCache = new Map();
export async function fetchGeoJSON(kind) {
  if (geoCache.has(kind)) return geoCache.get(kind);
  const url = LAYER_URLS[kind];
  if (!url) return null;
  try {
    const res = await fetch(url);
    const data = await res.json();
    geoCache.set(kind, data);
    return data;
  } catch {
    return null;
  }
}

// ---------- Curated volcano list (Smithsonian GVP subset, offline-safe) ----------
// alertLevel: 1=Normal (yellow), 2=Waspada (amber), 3=Siaga (orange), 4=Awas (red)
export const VOLCANO_ALERTS = {
  1: { label: "Normal", color: "#facc15" },
  2: { label: "Waspada", color: "#f59e0b" },
  3: { label: "Siaga", color: "#f97316" },
  4: { label: "Awas", color: "#dc2626" },
};

export const VOLCANOES = [
  { id: "krakatau", name: "Anak Krakatau", country: "Indonesia", latitude: -6.102, longitude: 105.423, elevation: 157, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "merapi", name: "Merapi", country: "Indonesia", latitude: -7.541, longitude: 110.446, elevation: 2910, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "semeru", name: "Semeru", country: "Indonesia", latitude: -8.108, longitude: 112.922, elevation: 3676, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "sinabung", name: "Sinabung", country: "Indonesia", latitude: 3.170, longitude: 98.392, elevation: 2460, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "agung", name: "Agung", country: "Indonesia", latitude: -8.343, longitude: 115.508, elevation: 3142, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "bromo", name: "Bromo", country: "Indonesia", latitude: -7.942, longitude: 112.95, elevation: 2329, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "kelud", name: "Kelud", country: "Indonesia", latitude: -7.93, longitude: 112.308, elevation: 1731, type: "Stratovolcano", status: "Active", alertLevel: 1 },
  { id: "raung", name: "Raung", country: "Indonesia", latitude: -8.119, longitude: 114.056, elevation: 3260, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "lokon", name: "Lokon-Empung", country: "Indonesia", latitude: 1.358, longitude: 124.792, elevation: 1580, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "soputan", name: "Soputan", country: "Indonesia", latitude: 1.108, longitude: 124.73, elevation: 1785, type: "Stratovolcano", status: "Active", alertLevel: 1 },
  { id: "ibu", name: "Ibu", country: "Indonesia", latitude: 1.488, longitude: 127.63, elevation: 1325, type: "Stratovolcano", status: "Active", alertLevel: 4 },
  { id: "lewotobi", name: "Lewotobi Laki-laki", country: "Indonesia", latitude: -8.53, longitude: 122.775, elevation: 1584, type: "Stratovolcano", status: "Active", alertLevel: 4 },
  { id: "marapi", name: "Marapi", country: "Indonesia", latitude: -0.381, longitude: 100.473, elevation: 2891, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "ruang", name: "Ruang", country: "Indonesia", latitude: 2.28, longitude: 125.37, elevation: 725, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "fuji", name: "Fuji", country: "Japan", latitude: 35.361, longitude: 138.727, elevation: 3776, type: "Stratovolcano", status: "Dormant", alertLevel: 1 },
  { id: "aso", name: "Aso", country: "Japan", latitude: 32.884, longitude: 131.104, elevation: 1592, type: "Caldera", status: "Active", alertLevel: 2 },
  { id: "sakurajima", name: "Sakurajima", country: "Japan", latitude: 31.585, longitude: 130.657, elevation: 1117, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "mayon", name: "Mayon", country: "Philippines", latitude: 13.257, longitude: 123.685, elevation: 2462, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "pinatubo", name: "Pinatubo", country: "Philippines", latitude: 15.13, longitude: 120.35, elevation: 1486, type: "Stratovolcano", status: "Dormant", alertLevel: 1 },
  { id: "taal", name: "Taal", country: "Philippines", latitude: 14.002, longitude: 120.993, elevation: 311, type: "Caldera", status: "Active", alertLevel: 2 },
  { id: "etna", name: "Etna", country: "Italy", latitude: 37.748, longitude: 14.999, elevation: 3357, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "vesuvius", name: "Vesuvius", country: "Italy", latitude: 40.821, longitude: 14.426, elevation: 1281, type: "Stratovolcano", status: "Dormant", alertLevel: 1 },
  { id: "stromboli", name: "Stromboli", country: "Italy", latitude: 38.789, longitude: 15.213, elevation: 924, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "st-helens", name: "Mount St. Helens", country: "USA", latitude: 46.2, longitude: -122.18, elevation: 2549, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "kilauea", name: "Kīlauea", country: "USA", latitude: 19.421, longitude: -155.287, elevation: 1247, type: "Shield", status: "Active", alertLevel: 3 },
  { id: "mauna-loa", name: "Mauna Loa", country: "USA", latitude: 19.475, longitude: -155.608, elevation: 4170, type: "Shield", status: "Active", alertLevel: 2 },
  { id: "yellowstone", name: "Yellowstone", country: "USA", latitude: 44.428, longitude: -110.588, elevation: 2805, type: "Caldera", status: "Active", alertLevel: 1 },
  { id: "popocatepetl", name: "Popocatépetl", country: "Mexico", latitude: 19.023, longitude: -98.622, elevation: 5426, type: "Stratovolcano", status: "Active", alertLevel: 3 },
  { id: "cotopaxi", name: "Cotopaxi", country: "Ecuador", latitude: -0.677, longitude: -78.436, elevation: 5911, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "villarrica", name: "Villarrica", country: "Chile", latitude: -39.42, longitude: -71.93, elevation: 2847, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "erebus", name: "Erebus", country: "Antarctica", latitude: -77.53, longitude: 167.17, elevation: 3794, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "eyja", name: "Eyjafjallajökull", country: "Iceland", latitude: 63.63, longitude: -19.62, elevation: 1651, type: "Stratovolcano", status: "Dormant", alertLevel: 1 },
  { id: "kambalny", name: "Kambalny", country: "Russia", latitude: 51.306, longitude: 156.874, elevation: 2156, type: "Stratovolcano", status: "Active", alertLevel: 2 },
  { id: "shiveluch", name: "Shiveluch", country: "Russia", latitude: 56.653, longitude: 161.36, elevation: 3283, type: "Stratovolcano", status: "Active", alertLevel: 4 },
];

// ---------- Megathrust Indonesia (approx polylines) ----------
export const MEGATHRUST_ZONES = [
  { name: "Sunda Megathrust (Sumatra)", coords: [[6, 92],[5, 94],[2, 96],[-1, 99],[-4, 102],[-6, 104]] },
  { name: "Sunda Megathrust (Jawa)", coords: [[-9, 105],[-10, 108],[-10.5, 112],[-10.5, 115],[-11, 118]] },
  { name: "Nusa Tenggara Trench", coords: [[-11, 118],[-11, 122],[-10.5, 126],[-10, 130]] },
  { name: "North Sulawesi Trench", coords: [[3, 121],[3.5, 123],[3, 125],[2, 127]] },
  { name: "Philippine Trench", coords: [[8, 127],[10, 127],[12, 126],[14, 125]] },
];

export const REGION_PRESETS = {
  indonesia: { center: [-2.5, 118], zoom: 5, lat: -2.5, lng: 118, altitude: 1.5 },
  global: { center: [10, 20], zoom: 2, lat: 10, lng: 20, altitude: 2.5 },
  east_asia: { center: [20, 125], zoom: 4, lat: 20, lng: 125, altitude: 1.8 },
  ring_of_fire: { center: [0, 160], zoom: 3, lat: 0, lng: 160, altitude: 2.1 },
  americas: { center: [10, -90], zoom: 3, lat: 10, lng: -90, altitude: 2.1 },
};
